/**
 * Supplier Returns Table Component
 *
 * Data table for displaying supplier returns.
 * @see US-40.3 - Frontend retours fournisseur
 */

import { useMemo, useCallback } from 'react';
import { Check, X, Trash2, MoreVertical, Pencil } from 'lucide-react';
import {
  DataGrid,
  Badge,
  Dropdown,
  DropdownItem,
  textColumn,
  dateTimeColumn,
  integerColumn,
  moneyColumn,
  type DataGridColumn,
  type DataGridSortState,
} from '@bill/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import type { SupplierReturn } from '@bstock/shared';
import {
  SupplierReturnStatus,
  SUPPLIER_RETURN_STATUS_LABELS,
  SUPPLIER_RETURN_STATUS_COLORS,
} from '@bstock/shared';

interface SupplierReturnsTableProps {
  returns: SupplierReturn[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  sortBy?: string;
  sortOrder?: string;
  onRowClick: (ret: SupplierReturn) => void;
  onValidate?: (ret: SupplierReturn) => void;
  onCancel?: (ret: SupplierReturn) => void;
  onDelete?: (ret: SupplierReturn) => void;
  onEdit?: (ret: SupplierReturn) => void;
  onSort: (field: string, order: string) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onFetchAllRows?: () => Promise<SupplierReturn[]>;
  showFinancials?: boolean;
}

// SupplierReturn has no backend sortBy DTO — only returnNumber and returnDate
// are exposed for sorting (based on original allowSorting flags)
const SORT_FIELD_MAP: Record<string, string> = {
  returnNumber: 'returnNumber',
  returnDate: 'returnDate',
  totalExclTax: 'totalExclTax',
};

/**
 * Map status to badge variant
 */
function getStatusVariant(status: SupplierReturnStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  const colorMap: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
    green: 'success',
    gray: 'neutral',
    red: 'danger',
  };
  return colorMap[SUPPLIER_RETURN_STATUS_COLORS[status]] ?? 'neutral';
}

export function SupplierReturnsTable({
  returns,
  pagination,
  isLoading,
  sortBy,
  sortOrder,
  onRowClick,
  onValidate,
  onCancel,
  onDelete,
  onEdit,
  onSort,
  onPageChange,
  onLimitChange,
  onFetchAllRows,
  showFinancials = true,
}: SupplierReturnsTableProps) {
  const currency = useCurrency();
  const { formatDateTime } = useFormatDate();

  const hasAnyAction = onValidate || onCancel || onDelete || onEdit;

  const columns = useMemo<DataGridColumn<SupplierReturn>[]>(() => {
    const cols: DataGridColumn<SupplierReturn>[] = [
      textColumn<SupplierReturn>({
        id: 'returnNumber',
        header: 'Numero',
        accessor: 'returnNumber',
      }),
      dateTimeColumn<SupplierReturn>({
        id: 'returnDate',
        header: 'Date',
        accessor: (r) => r.returnDate,
        cell: (r) => <>{formatDateTime(r.returnDate)}</>,
        exportValue: (r) => r.returnDate,
      }),
      textColumn<SupplierReturn>({
        id: 'supplierName',
        header: 'Fournisseur',
        sortable: false,
        accessor: 'supplierName',
        cell: (r) => (
          <div className="flex items-center gap-2">
            <span>{r.supplierName}</span>
            {/* US-SR-CENTRAL.1 : badge pour les retours vers la centrale interne. */}
            {r.isInternalReturn && (
              <Badge variant="info">Vers centrale</Badge>
            )}
          </div>
        ),
      }),
      textColumn<SupplierReturn>({
        id: 'reason',
        header: 'Motif',
        sortable: false,
        accessor: (r) => r.reason ?? '-',
        cell: (r) => (
          <span className="block max-w-[200px] truncate" title={r.reason ?? undefined}>
            {r.reason ?? '-'}
          </span>
        ),
        ellipsis: true,
        maxWidth: '200px',
      }),
      integerColumn<SupplierReturn>({
        id: 'itemCount',
        header: 'Articles',
        sortable: false,
        align: 'center',
        accessor: 'itemCount',
      }),
    ];

    if (showFinancials) {
      cols.push(
        moneyColumn<SupplierReturn>({
          id: 'totalExclTax',
          header: 'Total HT',
          accessor: (r) => r.totalExclTax / 100,
          cell: (r) => <>{formatCentimes(r.totalExclTax, currency)}</>,
          exportValue: (r) => r.totalExclTax / 100,
        })
      );
    }

    cols.push(
      textColumn<SupplierReturn>({
        id: 'status',
        header: 'Statut',
        sortable: false,
        align: 'center',
        accessor: (r) => SUPPLIER_RETURN_STATUS_LABELS[r.status],
        cell: (r) => (
          <div className="flex items-center justify-center">
            <Badge variant={getStatusVariant(r.status)}>
              {SUPPLIER_RETURN_STATUS_LABELS[r.status]}
            </Badge>
          </div>
        ),
      })
    );

    if (hasAnyAction) {
      cols.push(
        textColumn<SupplierReturn>({
          id: 'actions',
          header: '',
          sortable: false,
          exportSkip: true,
          width: '50px',
          cell: (r) => {
            const isDraft = r.status === SupplierReturnStatus.DRAFT;
            const isValidated = r.status === SupplierReturnStatus.VALIDATED;

            const showEdit = onEdit && isDraft;
            const showValidate = onValidate && isDraft;
            const showDelete = onDelete && isDraft;
            const showCancel = onCancel && isValidated;

            if (!showEdit && !showValidate && !showDelete && !showCancel) return null;

            return (
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown
                  trigger={
                    <button className="p-1 rounded hover:bg-neutral-100">
                      <MoreVertical className="w-4 h-4 text-neutral-500" />
                    </button>
                  }
                >
                  {showEdit && (
                    <DropdownItem
                      icon={<Pencil className="w-4 h-4" />}
                      onClick={() => onEdit(r)}
                    >
                      Modifier
                    </DropdownItem>
                  )}
                  {showValidate && (
                    <DropdownItem
                      icon={<Check className="w-4 h-4" />}
                      onClick={() => onValidate(r)}
                    >
                      Valider
                    </DropdownItem>
                  )}
                  {showDelete && (
                    <DropdownItem
                      icon={<Trash2 className="w-4 h-4" />}
                      onClick={() => onDelete(r)}
                      className="text-danger-600 hover:bg-danger-50"
                    >
                      Supprimer
                    </DropdownItem>
                  )}
                  {showCancel && (
                    <DropdownItem
                      icon={<X className="w-4 h-4" />}
                      onClick={() => onCancel(r)}
                      className="text-danger-600 hover:bg-danger-50"
                    >
                      Annuler
                    </DropdownItem>
                  )}
                </Dropdown>
              </div>
            );
          },
        })
      );
    }

    return cols;
  }, [currency, formatDateTime, onEdit, onValidate, onCancel, onDelete, showFinancials, hasAnyAction]);

  const sortState = useMemo<DataGridSortState | null>(() => {
    if (!sortBy || !sortOrder) return null;
    const columnId = Object.entries(SORT_FIELD_MAP).find(([, v]) => v === sortBy)?.[0];
    if (!columnId) return null;
    return { columnId, direction: sortOrder as 'asc' | 'desc' };
  }, [sortBy, sortOrder]);

  const handleSortChange = useCallback(
    (next: DataGridSortState | null) => {
      if (!next) {
        onSort('returnDate', 'desc');
        return;
      }
      const backendField = SORT_FIELD_MAP[next.columnId];
      if (backendField) onSort(backendField, next.direction);
    },
    [onSort]
  );

  return (
    <DataGrid<SupplierReturn>
      data={returns}
      columns={columns}
      rowKey={(r) => r.id}
      isLoading={isLoading}
      skeletonRows={8}
      onRowClick={onRowClick}
      currency={currency}
      sortState={sortState}
      onSortChange={handleSortChange}
      exportable={{
        fileName: { base: 'retours-fournisseur' },
        sheetName: 'Retours Fournisseur',
        position: 'header',
        onFetchAllRows,
      }}
      pagination={{
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        totalPages: pagination.totalPages,
        onPageChange,
        onLimitChange,
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucun retour fournisseur trouve
        </div>
      }
    />
  );
}
